import type { ApiErrorResponse } from '@/types/api';
import type { AxiosError } from '@umijs/max';

const DEFAULT_ERROR_MESSAGE = 'Ocurrió un error. Por favor intentá nuevamente.';
const STATUS_ERROR_MESSAGES: Record<number, string> = {
  502: 'El servicio no está disponible en este momento. Por favor intentá nuevamente en unos minutos.',
  503: 'El servicio no está disponible en este momento. Por favor intentá nuevamente en unos minutos.',
  504: 'El servicio tardó demasiado en responder. Por favor intentá nuevamente en unos minutos.',
};

const deriveStatusCode = (error: AxiosError<ApiErrorResponse>) =>
  error?.response?.status ?? error?.response?.data?.statusCode;

export const normalizeApiError = (error: unknown): ApiErrorResponse => {
  if (error instanceof Error) {
    if (error.message === 'Network Error') {
      return {
        message:
          'Error de red. Por favor comprobá tu conexión a internet e intentá nuevamente.',
      };
    }
    if (error.message.toLowerCase().includes('timeout')) {
      return {
        message:
          'Se agotó el tiempo de espera. Por favor, comprobá tu conexión a internet e intentá nuevamente.',
      };
    }
  }

  const axiosError = error as AxiosError<ApiErrorResponse>;
  const statusCode = deriveStatusCode(axiosError);
  const payload = axiosError?.response?.data;
  if (payload?.message || payload?.messages) {
    return {
      ...payload,
      statusCode: payload.statusCode ?? statusCode,
    };
  }

  if (statusCode && STATUS_ERROR_MESSAGES[statusCode]) {
    return {
      message: STATUS_ERROR_MESSAGES[statusCode],
      statusCode,
    };
  }

  const asApiError = error as ApiErrorResponse;
  if (asApiError?.message || asApiError?.messages) {
    return asApiError;
  }

  if (error instanceof Error) {
    return { message: error.message };
  }

  return { message: DEFAULT_ERROR_MESSAGE };
};

export const getApiErrorMessage = (
  error: unknown,
  fallback?: string,
): string => {
  const normalized = normalizeApiError(error);
  const messages = normalized.messages?.filter(Boolean);
  if (messages && messages.length > 0) {
    return messages.join(', ');
  }
  return normalized.message ?? fallback ?? DEFAULT_ERROR_MESSAGE;
};

export const DEFAULT_API_ERROR_MESSAGE = DEFAULT_ERROR_MESSAGE;
